import { TypedDocumentNode, gql } from '@apollo/client';
import { isFuture, isPast } from 'date-fns';
import styled from 'styled-components';

import {
  MarketplaceSpecialEventsType,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import Scrollable from '@sorare/core/src/atoms/navigation/Scrollable';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useToggle from '@sorare/core/src/hooks/useToggle';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { StarUniqueAuction } from './StarUniqueAuction';
import {
  StarUniqueAuctionCarouselQuery,
  StarUniqueAuctionCarouselQueryVariables,
} from './__generated__/index.graphql';

const STAR_UNIQUE_AUCTION_CAROUSEL_QUERY = gql`
  query StarUniqueAuctionCarouselQuery($assetIds: [String!]) {
    anyCards(assetIds: $assetIds) {
      slug
      ...StarUniqueAuction_anyCard
    }
  }
  ${StarUniqueAuction.fragments.anyCard}
` as TypedDocumentNode<
  StarUniqueAuctionCarouselQuery,
  StarUniqueAuctionCarouselQueryVariables
>;

const StyledSkeleton = styled(Skeleton)<{ expanded: boolean }>`
  width: 100%;
  height: ${({ expanded }) => (expanded ? '420px' : '188px')};

  @media ${tabletAndAbove} {
    height: ${({ expanded }) => (expanded ? '448px' : '203px')};
  }
`;

export const StarUniqueAuctionCarousel = () => {
  const { sport: sportContext = Sport.FOOTBALL } = useSportContext();
  const { currentUser } = useCurrentUserContext();
  const cardsCount = {
    [Sport.FOOTBALL]: currentUser?.footballUserProfile?.cardsCount,
    [Sport.NBA]: currentUser?.nbaUserProfile?.cardsCount,
    [Sport.BASEBALL]: currentUser?.baseballUserProfile?.cardsCount,
  }[sportContext];

  const { marketplaceSpecialEvents } = useConfigContext();
  const [expanded, toggleExpanded] = useToggle((cardsCount?.unique || 0) > 0);
  const sportSpecialEvents =
    marketplaceSpecialEvents.find(({ sport }) => sportContext === sport)
      ?.events || [];

  const starUniqueEvents = sportSpecialEvents.filter(
    event =>
      event.type === MarketplaceSpecialEventsType.STAR_UNIQUES &&
      event.endDate &&
      isFuture(event.endDate) &&
      event.startDate &&
      isPast(event.startDate)
  );
  const hideCarousel =
    cardsCount &&
    cardsCount.unique + cardsCount.superRare + cardsCount.rare === 0;

  const { data, loading } = useQuery(STAR_UNIQUE_AUCTION_CAROUSEL_QUERY, {
    variables: {
      assetIds: starUniqueEvents
        .flatMap(event => event.assetIds)
        .filter(Boolean),
    },
    skip: !starUniqueEvents.length || hideCarousel,
  });

  if (!starUniqueEvents.length || hideCarousel) return null;

  return (
    <Scrollable itemToDisplay={1} contained itemGap={0}>
      {loading ? (
        <StyledSkeleton expanded={expanded} />
      ) : (
        data?.anyCards.map(card => (
          <StarUniqueAuction
            key={card.slug}
            card={card}
            expanded={expanded}
            onToggle={toggleExpanded}
          />
        ))
      )}
    </Scrollable>
  );
};
